import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Wallet, TrendDown, Timer, WarningCircle } from "@phosphor-icons/react";

const money = (v) => `€${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function Runway() {
  const [data, setData] = useState(null);
  const [cash, setCash] = useState("");
  const [rev, setRev] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/runway/summary");
    setData(r.data);
    setCash(r.data.cash_source === "manual" ? String(r.data.cash_on_hand) : "");
    setRev(r.data.monthly_revenue ? String(r.data.monthly_revenue) : "");
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const body = {};
    if (cash !== "") body.cash_on_hand = Number(cash);
    if (rev !== "") body.monthly_revenue = Number(rev);
    await api.put("/runway/settings", body);
    toast.success("Runway updated");
    load();
  };

  if (!data) return <div className="text-white/40 text-sm">Loading…</div>;
  const positive = data.runway_months === null;
  const critical = data.runway_months !== null && data.runway_months < 6;

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-2">Cash Runway</div>
        <h1 className="font-display text-4xl">How long does your cash last?</h1>
        <p className="text-white/60 text-sm mt-2">Your survival gauge — cash on hand against real burn (payroll + spend) net of revenue, over the last {data.window_months} months.</p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Wallet} label="Cash on hand" value={money(data.cash_on_hand)} hint={data.cash_source === "bank" ? "from Bank Import" : data.cash_source === "manual" ? "set manually" : "not set"} />
        <Kpi icon={TrendDown} label="Net monthly burn" value={money(data.net_burn)} hint={positive ? "cash-flow positive" : "per month"} color={positive ? "#00A86B" : "#FFB020"} />
        <Kpi icon={Timer} label="Runway" value={positive ? "∞" : `${data.runway_months} mo`} hint={positive ? "revenue covers burn" : "at current burn"} color={positive ? "#00A86B" : critical ? "#E63946" : "#F3F5F4"} />
        <Kpi icon={WarningCircle} label="Zero-cash date" value={data.zero_date || "—"} hint={data.zero_date ? "projected" : "no burn"} color={critical ? "#E63946" : undefined} />
      </div>

      {critical && (
        <div className="glass rounded-xl p-4 flex items-center gap-3 border border-[#E63946]/30" data-testid="runway-alert">
          <WarningCircle size={20} weight="fill" className="text-[#E63946]" />
          <span className="text-sm text-white/80">Under 6 months of runway — time to plan a raise, cut burn, or accelerate revenue.</span>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Projection */}
        <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="runway-projection">
          <div className="eyebrow mb-4">12-month cash projection</div>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={data.projection}>
                <defs>
                  <linearGradient id="cashFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFB020" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#FFB020" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fill: "#9BA3A0", fontSize: 10 }} tickFormatter={(m) => `M${m}`} />
                <YAxis tick={{ fill: "#9BA3A0", fontSize: 10 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: "#0A1311", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10 }} formatter={(v) => money(v)} labelFormatter={(m) => `Month ${m}`} />
                <Area type="monotone" dataKey="balance" stroke="#FFB020" strokeWidth={2} fill="url(#cashFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Inputs + composition */}
        <div className="space-y-6">
          <div className="glass rounded-2xl p-6" data-testid="runway-inputs">
            <div className="eyebrow mb-4">Adjust</div>
            <div className="space-y-4">
              <div><label>Cash on hand (€)</label><input type="number" data-testid="runway-cash" placeholder={data.bank_balance != null ? `Bank: ${money(data.bank_balance)}` : "Enter balance"} value={cash} onChange={(e) => setCash(e.target.value)} /></div>
              <div><label>Monthly revenue (€)</label><input type="number" data-testid="runway-revenue" value={rev} onChange={(e) => setRev(e.target.value)} /></div>
              <button onClick={save} className="btn-gold w-full justify-center" data-testid="runway-save">Update runway</button>
            </div>
          </div>
          <div className="glass rounded-2xl p-6" data-testid="runway-composition">
            <div className="eyebrow mb-4">Monthly burn breakdown</div>
            {data.composition.map((c) => {
              const total = data.composition.reduce((s, x) => s + x.value, 0) || 1;
              return (
                <div key={c.label} className="mb-3">
                  <div className="flex justify-between text-sm mb-1"><span className="text-white/70">{c.label}</span><span className="mono text-white/60">{money(c.value)}</span></div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-[#FFB020]" style={{ width: `${Math.min(100, (c.value / total) * 100)}%` }} /></div>
                </div>
              );
            })}
            <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5">
              <div className="flex justify-between text-sm"><span className="text-white/70">Base revenue</span><span className="mono text-white/60">{money(data.monthly_revenue)}/mo</span></div>
              <div className="flex justify-between text-sm" data-testid="runway-invoiced-line">
                <span className="text-[#00A86B]">+ Paid invoices</span>
                <span className="mono text-[#00A86B]">{money(data.monthly_invoiced)}/mo</span>
              </div>
              <div className="text-[11px] text-white/40">Revenue from invoices you&apos;ve marked paid, averaged over {data.window_months} months.</div>
            </div>
          </div>
        </div>
      </div>
      <p className="text-xs text-white/40">Estimate based on the last {data.window_months} months of recorded payroll and receipts. Adjust cash and revenue for a truer picture.</p>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint, color }) {
  return (
    <div className="glass rounded-xl p-5" data-testid={`runway-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-2 text-white/50"><Icon size={16} /><span className="text-[10px] uppercase tracking-[0.2em]">{label}</span></div>
      <div className="mono text-2xl mt-2" style={color ? { color } : undefined}>{value}</div>
      {hint ? <div className="text-[11px] text-white/45 mt-1">{hint}</div> : null}
    </div>
  );
}
